import React, { useEffect, useMemo, useState } from "react";
import {
    Calendar,
    CalendarDays,
    CheckCircle2,
    Hourglass,
    Lightbulb,
    Send,
    ShieldCheck,
    Target,
} from "lucide-react";

import "../../RessourcesStyles/DemandeTrackingStepper.css";

const ICON_BY_CODE = {
    DEMANDEE: Send,
    VALIDATION: Hourglass,
    TRAITEE: ShieldCheck,
    PLANIFIEE: CalendarDays,
    REALISEE: Target,
    EXPERIMENTEE: Lightbulb,
};

const safe = (value) => (value != null && String(value).trim() !== "" ? String(value) : null);

const getStepStatusLabel = (status) => {
    switch ((status || "").toUpperCase()) {
        case "DONE":
            return "Terminé";
        case "CURRENT":
            return "En cours";
        case "PENDING":
            return "À venir";
        case "REJECTED":
            return "Rejeté";
        case "SKIPPED":
            return "Non applicable";
        default:
            return "";
    }
};

const getStepStatusClass = (status) => {
    switch ((status || "").toUpperCase()) {
        case "DONE":
            return "tracking-status-done";
        case "CURRENT":
            return "tracking-status-current";
        case "PENDING":
            return "tracking-status-pending";
        case "REJECTED":
            return "tracking-status-rejected";
        case "SKIPPED":
            return "tracking-status-skipped";
        default:
            return "tracking-status-pending";
    }
};

const getStepLabel = (step) => {
    if (step?.label) return step.label;

    switch (step?.code) {
        case "DEMANDEE":
            return "Demandée";
        case "VALIDATION":
            return "Validation";
        case "TRAITEE":
            return "Traitée";
        case "PLANIFIEE":
            return "Planifiée";
        case "REALISEE":
            return "Réalisée";
        case "EXPERIMENTEE":
            return "Expérimentée";
        default:
            return "";
    }
};

const formatTrackingDate = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const getStepIcon = (code) => {
    const Icon = ICON_BY_CODE[code] || CheckCircle2;
    return <Icon size={18} />;
};

export default function DemandeTrackingStepper({ tracking, onNavigate }) {
    const [selectedStepCode, setSelectedStepCode] = useState(null);

    const steps = useMemo(
        () => (Array.isArray(tracking?.steps) ? tracking.steps : []),
        [tracking?.steps]
    );

    useEffect(() => {
        if (tracking?.currentStep) {
            setSelectedStepCode(tracking.currentStep);
            return;
        }

        if (steps.length) {
            const current = steps.find((step) => {
                const status = String(step?.status || "").toUpperCase();
                return status === "CURRENT" || status === "REJECTED";
            });
            setSelectedStepCode(current?.code || steps[0]?.code || null);
            return;
        }

        setSelectedStepCode(null);
    }, [steps, tracking?.currentStep]);

    const selectedStep = useMemo(() => {
        if (!steps.length) return null;
        return steps.find((step) => step.code === selectedStepCode) || steps[0] || null;
    }, [selectedStepCode, steps]);

    const handleStepClick = (step) => {
        setSelectedStepCode(step?.code || null);
    };

    if (!steps.length) {
        return null;
    }

    return (
        <section className="demande-tracking-card">
            <div className="demande-tracking-header">
                <Calendar size={18} />
                <h3>Suivi de la demande</h3>
            </div>

            <div className="demande-tracking-steps">
                {steps.map((step, index) => {
                    const statusClass = getStepStatusClass(step?.status);
                    const isSelected = selectedStepCode === step?.code;

                    return (
                        <button
                            type="button"
                            key={step.code || step.order || index}
                            className={`demande-tracking-step ${isSelected ? "is-selected" : ""}`}
                            onClick={() => handleStepClick(step)}
                        >
                            {index < steps.length - 1 && (
                                <span className={`tracking-step-line ${statusClass}`} />
                            )}

                            <span className={`tracking-step-icon ${statusClass}`}>
                                {getStepIcon(step?.code)}
                            </span>

                            <span className="tracking-step-title">{getStepLabel(step)}</span>
                        </button>
                    );
                })}
            </div>

            {selectedStep && (
                <div className={`tracking-selected-detail ${getStepStatusClass(selectedStep.status)}`}>
                    <div className="tracking-selected-detail-top">
                        <div>
                            <span className={`tracking-status-badge ${getStepStatusClass(selectedStep.status)}`}>
                                {safe(selectedStep.statusLabel) || getStepStatusLabel(selectedStep.status)}
                            </span>
                            <h4>{getStepLabel(selectedStep)}</h4>
                        </div>
                    </div>

                    <div className="tracking-selected-detail-content">
                        {selectedStep.description && <p>{selectedStep.description}</p>}

                        <div className="tracking-selected-meta">
                            {selectedStep.date && (
                                <span>Date : {formatTrackingDate(selectedStep.date)}</span>
                            )}

                            {selectedStep.actor && (
                                <span>Acteur : {selectedStep.actor}</span>
                            )}
                        </div>

                        {selectedStep.motif && (
                            <div className="tracking-selected-motif">
                                Motif : {selectedStep.motif}
                            </div>
                        )}

                        {selectedStep.clickable && selectedStep.targetUrl && onNavigate && (
                            <button
                                type="button"
                                className="tracking-detail-action"
                                onClick={() => onNavigate(selectedStep.targetUrl, selectedStep)}
                            >
                                Voir dans l&apos;agenda
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
